import React from 'react';
import c from 'classnames';
import { ThemeOptions, ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { useTranslation } from 'react-i18next';
import CloseBold from '../../../Icons/CloseBold';
import LargeSophisticatedTooltipContent from '../../LargeSophisticatedTooltipContent';
import MediumSophisticatedTooltipContent from '../../MediumSophisticatedTooltipContent';
import IconButton from '../../../Buttons/IconButton';
import PopoverWrapper from '../../../popper/PopoverWrapper';
import type { PositionableProps } from '../../../popper/types';
import s from './SophisticatedTooltipContent.scss';

interface Props extends PositionableProps {
  /**
   * Will determine which of our sophisticated tooltip variants we render.
   */
  size: 'medium' | 'large';

  /**
   * The main body text of the tooltip. Displays after the heading.
   */
  description?: string | React.ReactNode;

  /**
   * A slot that displays before the heading. Depending on size, this could be above or to the
   * side of the main content. Consider putting an image here.
   */
  prefix?: React.ReactNode;

  /**
   * Displays after the heading and description. Consider putting a `<SophisticatedTooltipLink />` here.
   */
  postfix?: React.ReactNode;

  /**
   * Limits the width of the tooltip.
   */
  width?: number;

  /**
   *
   */
  hasCloseButton?: boolean;

  /**
   *
   */
  forceTheme?: ThemeOptions;
  className?: string;
  contentClassName?: string;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  onClose?: () => void;
}

interface PropsWithHeading extends Props {
  /**
   * The emphasized text in this tooltip. Displays before the description.
   */
  heading: string;
}

interface PropsWithAriaLabel extends Props {
  'aria-label': string;
}

type CombinedProps = PropsWithHeading | PropsWithAriaLabel;

/** A more sophisticated tooltip with a `heading` and `description`.  */
const SophisticatedTooltipContent: React.FC<CombinedProps> = ({
  width,
  description,
  prefix,
  postfix,
  size,
  hasCloseButton,
  forceTheme = ThemeOptions.LIGHT,
  effectivePlacement,
  effectiveSecondaryPlacement,
  rootRef,
  arrowRef,
  arrowAttributes,
  arrowStyle,
  contentStyle,
  contentAttributes,
  updatePositioning,
  onMouseEnter,
  onMouseLeave,
  onClose,
  className,
  contentClassName,
  ...rest
}) => {
  const { t } = useTranslation();
  const heading = (rest as PropsWithHeading).heading;
  const ariaLabel = (rest as PropsWithAriaLabel)['aria-label'];

  return (
    <PopoverWrapper
      wrapperClassName={className}
      className={contentClassName}
      contentClassName={c(s['popover-wrapper'], s[`size-${size}`])}
      width={width}
      onClickOutside={onClose}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      effectivePlacement={effectivePlacement}
      effectiveSecondaryPlacement={effectiveSecondaryPlacement}
      rootRef={rootRef}
      arrowRef={arrowRef}
      contentStyle={contentStyle}
      arrowStyle={arrowStyle}
      contentAttributes={contentAttributes}
      arrowAttributes={arrowAttributes}
      updatePositioning={updatePositioning}
      forceTheme={forceTheme}
    >
      <div className={c(hasCloseButton && s['close-button-padding'])}>
        <ThemeOverride theme={forceTheme}>
          {size === 'medium' ? (
            <MediumSophisticatedTooltipContent
              heading={heading}
              aria-label={ariaLabel}
              description={description}
              prefix={prefix}
              postfix={postfix}
            />
          ) : (
            <LargeSophisticatedTooltipContent
              heading={heading}
              aria-label={ariaLabel}
              description={description}
              prefix={prefix}
              postfix={postfix}
            />
          )}
        </ThemeOverride>
      </div>

      {hasCloseButton && (
        <IconButton
          icon={CloseBold}
          size="small"
          className={s['close-button']}
          onClick={onClose}
          aria-label={t('common.close')}
        />
      )}
    </PopoverWrapper>
  );
};
SophisticatedTooltipContent.displayName = 'SophisticatedTooltipContent';

export default SophisticatedTooltipContent;
